import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Check, Loader2 } from "lucide-react";

// Lightweight email-capture for "Notify me" flows (Desktop Timer interest,
// product-update opt-in). Posts to /api/notify-signups. If the user is logged in
// the backend falls back to their account email when none is supplied.
export function NotifyMe({
  topic = "product_updates",
  label = "Notify me about product updates",
  confirmation = "You're subscribed to NetLift product updates.",
  defaultEmail = "",
  buttonVariant = "outline",
  buttonClassName = "",
  size = "sm",
  testid = "notify-me",
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/notify-signups", { email, topic });
      if (data.ok) {
        setDone(true);
        setOpen(false);
        toast.success(confirmation);
      } else {
        toast.error(data.detail || "Please enter a valid email.");
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1E9E6A]" data-testid={`${testid}-done`}>
        <Check className="h-4 w-4" /> Subscribed
      </span>
    );
  }

  if (!open) {
    return (
      <Button variant={buttonVariant} size={size} className={buttonClassName} onClick={() => setOpen(true)} data-testid={`${testid}-open`}>
        <Bell className="mr-2 h-4 w-4" /> {label}
      </Button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testid}-form`}>
      <Input
        type="email"
        placeholder="you@company.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        className="h-9 w-56"
        data-testid={`${testid}-email`}
      />
      <Button size={size} className="bg-editorial hover:bg-editorial/90" onClick={submit} disabled={busy} data-testid={`${testid}-submit`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subscribe"}
      </Button>
      <Button size={size} variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
    </div>
  );
}
